import React, { useState, useEffect, useCallback } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service } from '@/services/pmt2Service';
import { Timestamp } from 'firebase/firestore';

interface StatusEntry {
  id: string;
  date: Timestamp;
  Date: string; // Added Date field (uppercase)
  'Your Name': string;
  'New Value': string;
  note: string;
  [key: string]: any;
}

interface PPCStatusHistoryDashboardProps {
  product: PMT2Product | null;
}

/**
 * PPC Status History Dashboard Component
 * Displays the history of PPC status changes for a selected product
 */
const PPCStatusHistoryDashboard: React.FC<PPCStatusHistoryDashboardProps> = ({ product }) => {
  const [statusHistory, setStatusHistory] = useState<StatusEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatusHistory = async () => {
      if (!product) {
        setStatusHistory([]);
        setError(null);
        return setLoading(false);
      }

      try {
        setLoading(true);
        const entries = await pmt2Service.getPPCStatusHistory(product.companyName, product.id);
        
        // Log the entries to debug date formats
        console.log('PPC Status entries:', entries);
        
        setStatusHistory(entries);
        setError(null);
      } catch (err) {
        console.error('Error fetching PPC status history:', err instanceof Error ? err.message : err);
        
        // If the error is related to Firestore path, provide a more helpful message
        if (err instanceof Error && err.message.includes('Invalid collection reference')) {
          setError(
            'Failed to load PPC status history due to a database path issue. ' +
            'Please ensure the PPC Status collection is properly configured.'
          );
          return setLoading(false);
        }
        
        setError('Failed to load PPC status history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatusHistory();
  }, [product]);

  // Sort entries by date (newest first)
  const sortedEntries = [...statusHistory].sort((a, b) => {
    // Use the Date field for sorting if available
    // First try using the Timestamp object if available
    if (a.date?.toDate && b.date?.toDate) {
      return b.date.toDate().getTime() - a.date.toDate().getTime();
    }
    
    // Next try using the Date string field
    else if (a.Date && b.Date) {
      // Check if the date includes time (contains ':')
      const hasTimeA = a.Date.includes(':');
      const hasTimeB = b.Date.includes(':');
      
      if (hasTimeA && hasTimeB) {
        // If both have time, parse directly
        return new Date(b.Date).getTime() - new Date(a.Date).getTime();
      }
      
      // Parse the date strings (format: MM/DD/YY)
      const partsA = a.Date.split('/');
      const partsB = b.Date.split('/');
      
      if (partsA.length === 3 && partsB.length === 3) {
        const yearA = 2000 + parseInt(partsA[2], 10);
        const monthA = parseInt(partsA[0], 10) - 1;
        const dayA = parseInt(partsA[1], 10);
        
        const yearB = 2000 + parseInt(partsB[2], 10);
        const monthB = parseInt(partsB[0], 10) - 1;
        const dayB = parseInt(partsB[1], 10);
        
        const dateA = new Date(yearA, monthA, dayA);
        const dateB = new Date(yearB, monthB, dayB);
        
        return dateB.getTime() - dateA.getTime();
      }
    }

    // Last resort: try to compare by days ago
    const daysAgoA = calculateDaysAgo(a.Date || '');
    const daysAgoB = calculateDaysAgo(b.Date || '');
    return daysAgoA - daysAgoB; // Smaller days ago means more recent
  });

  // Helper function to convert Excel/Google Sheets serial number to Date
  const serialNumberToDate = (serialNumber: number): Date => {
    // Excel/Google Sheets date serial number system starts from January 1, 1900
    // Serial number 1 = January 1, 1900
    const excelEpoch = new Date(1900, 0, 1);
    // Subtract 1 because Excel counts January 1, 1900 as day 1, not day 0
    const date = new Date(excelEpoch.getTime() + (serialNumber - 1) * 24 * 60 * 60 * 1000);
    return date;
  };

  // Calculate days ago from Date string
  const calculateDaysAgo = (dateStr: string): number => {
    if (!dateStr) return 0;
    
    try {
      let entryDate: Date;
      
      // Check if the dateStr is a serial number (pure number)
      const trimmedStr = dateStr.trim();
      if (/^\d+$/.test(trimmedStr)) {
        const serialNumber = parseInt(trimmedStr, 10);
        // Convert serial number to date
        entryDate = serialNumberToDate(serialNumber);
      } else {
        // Parse the date string (format: MM/DD/YY)
        const parts = dateStr.split('/');
        if (parts.length !== 3) return 0;

        const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed in JS Date
        const day = parseInt(parts[1], 10);
        
        // Handle both 2-digit and 4-digit years
        let year = parseInt(parts[2], 10);
        if (year < 100) {
          // If it's a 2-digit year, assume 20xx
          year = 2000 + year;
        }
        
        entryDate = new Date(year, month, day);
      }
      
      entryDate.setHours(0, 0, 0, 0);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Calculate the difference in days
      const diffTime = today.getTime() - entryDate.getTime();
      return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      return 0;
    }
  };

  // Format date string to "Month Day, Year" format
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return 'Unknown date';
    
    try {
      let date: Date;
      
      // Check if the dateStr is a serial number (pure number)
      const trimmedStr = dateStr.trim();
      if (/^\d+$/.test(trimmedStr)) {
        const serialNumber = parseInt(trimmedStr, 10);
        // Convert serial number to date
        date = serialNumberToDate(serialNumber);
      } else {
        // Check if the date includes time (contains ':')
        const hasTime = dateStr.includes(':');
        
        if (hasTime) {
          // If it has time, try to parse it directly
          date = new Date(dateStr);
        } else {
          // Otherwise, parse it as MM/DD/YY or MM/DD/YYYY
          const parts = dateStr.split('/');
          if (parts.length !== 3) return dateStr; // Return original if not in expected format
          
          const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed in JS Date
          const day = parseInt(parts[1], 10);
          let year = parseInt(parts[2], 10);
          if (year < 100) {
            year = 2000 + year;
          }
          
          date = new Date(year, month, day);
        }
      }
      
      // Format the date as "Month Day, Year"
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (error) {
      // If there's an error parsing, return the original string
      return dateStr;
    }
  };

  if (!product) return null;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">PPC Status History</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading status history...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">PPC Status History</h2>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (statusHistory.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">PPC Status History</h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm">No status history available for this product.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
      <h2 className="text-2xl font-bold mb-6">PPC Status History</h2>
      
      {sortedEntries.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
          No status history found for this product.
        </div>
      ) : (
        <div className="space-y-4">
          {sortedEntries.map((entry, index) => {
            // Use the Date field instead of date timestamp
            const daysAgo = calculateDaysAgo(entry.Date);
            const formattedDate = formatDate(entry.Date);
            
            return (
              <div key={`entry_${index}_${entry.id || Math.random().toString(36).substr(2, 9)}`} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-100 dark:bg-gray-700">
                <div className="flex flex-wrap gap-4 items-center mb-2 text-sm">
                  <span className="font-medium">{daysAgo} days ago</span>
                  <span className="text-gray-500 dark:text-gray-400">{formattedDate}</span>
                  <span className="text-gray-500 dark:text-gray-400">{entry['Your Name'] || 'Unknown'}</span>
                </div>
                
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Status</h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{entry['New Value'] || 'No status provided'}</p>
                </div>
                
                {entry.note && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Note</h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm">{entry.note}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PPCStatusHistoryDashboard;